/**
 * The front pictures (the first twelve, and the lofi station's twelve wide scenes). Each is its own module (loaded only when picked, so boot stays fast), tied to a
 * place or a school of the game. The front picks one at random per entry, never the one just seen (boot.ts).
 */
import type { MakeTheme } from './kit';

export type SceneId =
  | 'island'
  | 'storm'
  | 'fort'
  | 'moon'
  | 'snow'
  | 'mist'
  | 'temple'
  | 'camp'
  | 'valley'
  | 'hall'
  | 'bamboo'
  | 'stele'
  | 'lake'
  | 'falls'
  | 'lantern'
  | 'blossom'
  | 'peak'
  | 'pier'
  | 'village'
  | 'snowfield'
  | 'grove'
  | 'arena'
  | 'fireflies'
  | 'capital';

export interface SceneDef {
  id: SceneId;
  /** Thai name (the dev picker). */
  name: string;
  /** Which track and ambience it plays (front/audio.ts). */
  mood: 'login' | 'storm' | 'war' | 'moon' | 'calm' | 'bells' | 'night' | 'rain' | 'stele' | 'lofi';
  load: () => Promise<{ make: MakeTheme }>;
  /** One short Thai line under the picture, matching what the scene shows (original wording). */
  caption: string;
}

type SceneBase = Omit<SceneDef, 'caption'>;

const BASE: readonly SceneBase[] = [
  { id: 'island', name: 'เกาะลอยลานศิลา', mood: 'login', load: () => import('./island') },
  { id: 'storm', name: 'ศึกกลางพายุ', mood: 'storm', load: () => import('./storm') },
  { id: 'fort', name: 'ป้อมสำนักดาบโลหิต', mood: 'war', load: () => import('./fort') },
  { id: 'moon', name: 'จันทร์เพ็ญบนหลังคา', mood: 'moon', load: () => import('./moon') },
  { id: 'snow', name: 'ยอดเขาหิมะ', mood: 'storm', load: () => import('./snow') },
  { id: 'mist', name: 'ภูเขายอดหมอก', mood: 'calm', load: () => import('./mist') },
  { id: 'temple', name: 'วัดหินระฆัง', mood: 'bells', load: () => import('./temple') },
  { id: 'camp', name: 'ค่ายทางโคมเหล็ก', mood: 'war', load: () => import('./camp') },
  { id: 'valley', name: 'หุบเขาจันทร์มัว', mood: 'night', load: () => import('./valley') },
  { id: 'hall', name: 'หอหมึกสน', mood: 'rain', load: () => import('./hall') },
  { id: 'bamboo', name: 'ป่าไผ่ในสายฝน', mood: 'rain', load: () => import('./bamboo') },
  { id: 'stele', name: 'ศิลาเก้าร่อง', mood: 'stele', load: () => import('./stele') },
  // The lofi station's wide scenes (lane C0-LOFI): they play the station's playlist (front/station.ts).
  { id: 'lake', name: 'ศาลากลางทะเลสาบ', mood: 'lofi', load: () => import('./lake') },
  { id: 'falls', name: 'น้ำตกเขาหยก', mood: 'lofi', load: () => import('./falls') },
  { id: 'lantern', name: 'ตลาดโคมแดงยามค่ำ', mood: 'lofi', load: () => import('./lantern') },
  { id: 'blossom', name: 'ทุ่งซากุระริมธาร', mood: 'lofi', load: () => import('./blossom') },
  { id: 'peak', name: 'หอระฆังยอดหมอก', mood: 'lofi', load: () => import('./peak') },
  { id: 'pier', name: 'ท่าเรือริมทะเล', mood: 'lofi', load: () => import('./pier') },
  { id: 'village', name: 'หมู่บ้านนาข้าว', mood: 'lofi', load: () => import('./village') },
  { id: 'snowfield', name: 'ลานหิมะเงียบ', mood: 'lofi', load: () => import('./snowfield') },
  { id: 'grove', name: 'ป่าลึกแสงลอด', mood: 'lofi', load: () => import('./grove') },
  { id: 'arena', name: 'ลานประลองยามเที่ยง', mood: 'lofi', load: () => import('./arena') },
  { id: 'fireflies', name: 'ทุ่งหิ่งห้อยกลางคืน', mood: 'lofi', load: () => import('./fireflies') },
  { id: 'capital', name: 'ประตูเมืองหลวงยามเช้า', mood: 'lofi', load: () => import('./capital') },
];

const CAPTIONS: Record<SceneId, string> = {
  island: 'เกาะลอยกลางเมฆ ลานศิลาเงียบรอผู้มาถึง',
  storm: 'ลมฝนโหมกระหน่ำ สองฝ่ายปะทะกันบนทางลาด',
  fort: 'ธงแดงเหนือกำแพงป้อม ทหารเฝ้ายามไม่หลับ',
  moon: 'จันทร์เพ็ญเหนือหลังคา เงาหนึ่งยืนเผชิญหน้ากัน',
  snow: 'หิมะปลิวบนยอดเขา ผู้ฝึกยืนนิ่งท่ามกลางลมหนาว',
  mist: 'ศาลาบนเสาหินเหนือทะเลหมอก ยามอรุณเงาหนึ่งร่อนผ่านตะวัน',
  temple: 'เสียงระฆังก้องจากวัดหิน ผู้แสวงทางหยุดพักที่ลานหน้าประตู',
  camp: 'ไฟตะเกียงเรียงรายในค่าย ยามกลางคืนไม่มีใครวางกระบี่',
  valley: 'หุบเขาใต้จันทร์มัว สายน้ำไหลเงียบข้างสะพานไม้',
  hall: 'หอหมึกสนยามฝนพรำ ตำราและพู่กันรอผู้อ่าน',
  bamboo: 'ป่าไผ่ในสายฝน ใบเขียวเปียกชื้นกลางม่านหมอก',
  stele: 'ศิลาดำกลางลาน เก้าร่องกระบี่เรืองแสงใต้จันทร์แดง',
  lake: 'ศาลากลางทะเลสาบ เรือลำเล็กลอยเอื่อยบนน้ำนิ่ง',
  falls: 'น้ำตกไหลลงสระใต้ผาหยก ละอองน้ำลอยขึ้นเป็นหมอก',
  lantern: 'ตลาดโคมแดงยามค่ำ ผู้คนเดินเลือกของใต้แสงไฟ',
  blossom: 'ดอกท้อโปรยริมธาร กลีบสีชมพูลอยตามน้ำไป',
  peak: 'หอระฆังบนยอดเขายามเย็น แสงสุดท้ายทาเมฆเป็นสีทอง',
  pier: 'เรือจอดข้างท่า โคมริมน้ำส่องคลื่นยามเย็น',
  village: 'นาขั้นบันไดสะท้อนฟ้า ควันเตาลอยขึ้นจากหลังคาบ้าน',
  snowfield: 'ลานหิมะสงัด มีเพียงรอยเท้าและลมแผ่ว',
  grove: 'แสงเช้าลอดป่าลึก ผู้เฒ่าเล่านิทานให้เด็กฟังข้างกองฟืน',
  arena: 'ลานประลองยามเที่ยง แดดจ้าเหนือพื้นหินที่รอผู้ท้า',
  fireflies: 'กองไฟอุ่นข้างเต็นท์ หิ่งห้อยลอยเหนือทุ่งยามค่ำ',
  capital: 'ประตูเมืองหลวงยามเช้า ผู้คนทยอยเข้าตลาดตามถนนหิน',
};

export const SCENES: readonly SceneDef[] = BASE.map((b) => ({ ...b, caption: CAPTIONS[b.id] }));
